import { sha256 } from '@noble/hashes/sha2.js';
import { ripemd160 } from '@noble/hashes/legacy.js';

export const hex = {
  encode(b: Uint8Array): string {
    let s = '';
    for (const x of b) s += x.toString(16).padStart(2, '0');
    return s;
  },
  /** Returns null for anything that is not an even-length hex string. */
  decode(s: string): Uint8Array | null {
    if (s.length % 2 !== 0 || !/^[0-9a-fA-F]*$/.test(s)) return null;
    const out = new Uint8Array(s.length / 2);
    for (let i = 0; i < out.length; i++) out[i] = parseInt(s.slice(2 * i, 2 * i + 2), 16);
    return out;
  },
};

export function concat(...parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

export function equal(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let d = 0;
  for (let i = 0; i < a.length; i++) d |= a[i] ^ b[i];
  return d === 0;
}

export const sha256d = (b: Uint8Array): Uint8Array => sha256(sha256(b));
export const hash160 = (b: Uint8Array): Uint8Array => ripemd160(sha256(b));

/** Little-endian byte writer (Bitcoin serialisation). */
export class Writer {
  private parts: Uint8Array[] = [];

  bytes(b: Uint8Array): this {
    this.parts.push(b);
    return this;
  }
  u8(n: number): this {
    return this.bytes(Uint8Array.of(n & 0xff));
  }
  u32(n: number): this {
    const b = new Uint8Array(4);
    new DataView(b.buffer).setUint32(0, n >>> 0, true);
    return this.bytes(b);
  }
  i32(n: number): this {
    const b = new Uint8Array(4);
    new DataView(b.buffer).setInt32(0, n | 0, true);
    return this.bytes(b);
  }
  /** 64-bit amount (satoshis are always < 2^53 here). */
  u64(n: number): this {
    if (!Number.isSafeInteger(n) || n < 0) throw new Error('invalid amount');
    const b = new Uint8Array(8);
    new DataView(b.buffer).setBigUint64(0, BigInt(n), true);
    return this.bytes(b);
  }
  varint(n: number): this {
    if (n < 0xfd) return this.u8(n);
    if (n <= 0xffff) {
      const b = new Uint8Array(3);
      b[0] = 0xfd;
      new DataView(b.buffer).setUint16(1, n, true);
      return this.bytes(b);
    }
    this.u8(0xfe);
    return this.u32(n);
  }
  varBytes(b: Uint8Array): this {
    return this.varint(b.length).bytes(b);
  }
  done(): Uint8Array {
    return concat(...this.parts);
  }
}

export function randomBytes(n: number): Uint8Array {
  const b = new Uint8Array(n);
  crypto.getRandomValues(b);
  return b;
}
